import { useCallback, useEffect, useRef, useState } from "react";
import { useWebServer } from "../../../../../providers";
import { getSpecimen, listSpecimens } from "./specimenApi";

const PAGE = 50;

/**
 * What the specimen panel is stepping through, and the specimen it shows.
 *
 * open({title, filter}) lists the specimens matching a report filter body,
 * fetched a page at a time. open({title, keys, index}) steps through given
 * [{trayId, specimenId}] (the raw data grid's rows). `refresh()` reloads both
 * after an edit: a specimen that no longer matches drops out of the list, so
 * the same position then holds the next one.
 */
export const useSpecimenBrowser = (timezone) => {
  const { sendOrganizationRequest } = useWebServer();
  const send = useRef(sendOrganizationRequest);
  send.current = sendOrganizationRequest;

  const [source, setSource] = useState(null);
  const [view, setView] = useState("list");
  const [index, setIndex] = useState(0);
  const [pages, setPages] = useState({});
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [specimen, setSpecimen] = useState(null);
  const [specimenLoading, setSpecimenLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generation, setGeneration] = useState(0);

  const open = useCallback((next) => {
    setSource(next);
    setPages({});
    setSpecimen(null);
    setError(null);
    setIndex(next.index ?? 0);
    setTotal(next.keys ? next.keys.length : 0);
    // a raw data row opens on its specimen; a cell's specimens on their list
    setView(next.keys ? "specimen" : "list");
    setGeneration((g) => g + 1);
  }, []);
  const close = useCallback(() => setSource(null), []);

  const pageOf = (i) => Math.floor(i / PAGE);

  // the page holding `index`, for a filter source
  useEffect(() => {
    if (!source?.filter) return undefined;
    const p = pageOf(index);
    if (pages[p]) return undefined;
    let current = true;
    setListLoading(true);
    listSpecimens(send.current, source.filter, p, PAGE)
      .then((res) => {
        if (!current) return;
        // always an array: a missing page would be fetched again, forever
        setPages((prev) => ({ ...prev, [p]: Array.isArray(res?.items) ? res.items : [] }));
        setTotal(Number(res?.total) || 0);
      })
      .catch(() => current && setError("The specimens could not be loaded."))
      .finally(() => current && setListLoading(false));
    return () => {
      current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, index, pages, generation]);

  // the list shrank past where we were (the last one was edited away)
  useEffect(() => {
    if (source?.filter && total > 0 && index >= total) setIndex(total - 1);
  }, [source, total, index]);

  const keyAt = (i) => {
    if (!source) return null;
    if (source.keys) return source.keys[i] ?? null;
    return pages[pageOf(i)]?.[i % PAGE] ?? null;
  };
  const current = keyAt(index);

  // the specimen at `index`, once the list knows which it is
  useEffect(() => {
    if (!source || view !== "specimen" || !current) return undefined;
    let live = true;
    setSpecimenLoading(true);
    getSpecimen(send.current, current.trayId, current.specimenId, timezone)
      .then((data) => live && setSpecimen(data))
      .catch(() => live && setError("The specimen could not be loaded."))
      .finally(() => live && setSpecimenLoading(false));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, view, current?.trayId, current?.specimenId, generation]);

  const go = (i) => {
    setError(null);
    setIndex(Math.max(0, Math.min(total - 1, i)));
  };

  return {
    source,
    isOpen: Boolean(source),
    open,
    close,
    view,
    showList: () => setView("list"),
    showSpecimen: (i) => {
      setIndex(i);
      setView("specimen");
    },
    index,
    total,
    page: pageOf(index),
    pageSize: PAGE,
    items: source?.filter ? pages[pageOf(index)] ?? [] : [],
    listLoading,
    specimen,
    specimenLoading,
    error,
    hasPrevious: index > 0,
    hasNext: index < total - 1,
    previous: () => go(index - 1),
    next: () => go(index + 1),
    goToPage: (p) => go(p * PAGE),
    // after an edit: forget the loaded pages and the specimen, keep the place
    refresh: () => {
      setPages({});
      setGeneration((g) => g + 1);
    },
    /**
     * After saving `saved` ({trayId, specimenId}), go on to the next one. In a
     * filtered list the saved specimen may no longer match (an unknown that
     * now has an ID): then the list is reloaded and its place holds the next
     * one. If it still matches, step past it.
     */
    advanceFrom: async (saved) => {
      if (!source?.filter) {
        go(index + 1);
        return;
      }
      const p = pageOf(index);
      const res = await listSpecimens(send.current, source.filter, p, PAGE).catch(() => null);
      const items = Array.isArray(res?.items) ? res.items : [];
      const stillHere = items[index % PAGE];
      setPages({ [p]: items });
      setTotal(Number(res?.total) || 0);
      if (stillHere && stillHere.trayId === saved.trayId && stillHere.specimenId === saved.specimenId) {
        setIndex((i) => Math.min(i + 1, Math.max(0, (Number(res?.total) || 1) - 1)));
      } else {
        setGeneration((g) => g + 1);
      }
    },
    setSpecimen,
  };
};
